import { useEffect, useRef, useState } from 'react';

interface Props {
  videoUrl: string;
  initialTime?: number;
  onSubmit: (data: { time_seconds: number; video_duration: number }) => Promise<void>;
  onMarkUnlabelable: (data: { unlabelable: true; reason?: string }) => Promise<void>;
}

/**
 * 影片自動靜音播放。使用者拖曳時間軸至停止線通過時刻,觸控滑桿瞬間即會暫停。
 * 送出時將所選時間與影片總長度寫入標註。
 */
export default function VideoLabeler({
  videoUrl,
  initialTime,
  onSubmit,
  onMarkUnlabelable,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(initialTime ?? 0);
  const [touched, setTouched] = useState(initialTime !== undefined);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [videoErr, setVideoErr] = useState<string | null>(null);
  const [videoState, setVideoState] = useState<'loading' | 'ready' | 'error'>('loading');

  // 切換影片時重置狀態
  useEffect(() => {
    setDuration(0);
    setCurrent(initialTime ?? 0);
    setTouched(initialTime !== undefined);
    setErr(null);
    setVideoErr(null);
    setVideoState('loading');
  }, [videoUrl, initialTime]);

  function onVideoError(e: React.SyntheticEvent<HTMLVideoElement>) {
    const v = e.currentTarget;
    const mediaErr = v.error;
    const codes: Record<number, string> = {
      1: 'MEDIA_ERR_ABORTED — 載入被使用者中止',
      2: 'MEDIA_ERR_NETWORK — 網路錯誤(連線失敗 / CORS / 401 / 403 / 404 等)',
      3: 'MEDIA_ERR_DECODE — 解碼錯誤(檔案損毀或格式問題)',
      4: 'MEDIA_ERR_SRC_NOT_SUPPORTED — 無法取得來源(常見:404 或瀏覽器不支援格式)',
    };
    const detail = mediaErr
      ? codes[mediaErr.code] || `code=${mediaErr.code}: ${mediaErr.message}`
      : '原因不明';
    setVideoErr(detail);
    setVideoState('error');
  }

  // 自動播放時讓滑桿跟著 video 走;使用者一旦拖曳就停止跟隨
  function onTimeUpdate() {
    const v = videoRef.current;
    if (!v) return;
    if (!touched) setCurrent(v.currentTime);
  }

  function onLoadedMetadata() {
    const v = videoRef.current;
    if (!v) return;
    setDuration(v.duration || 0);
    setVideoState('ready');
    if (initialTime !== undefined && isFinite(initialTime)) {
      v.currentTime = initialTime;
      v.pause();
    }
  }

  function onScrub(e: React.ChangeEvent<HTMLInputElement>) {
    const v = videoRef.current;
    if (!v) return;
    const t = Number(e.target.value);
    setCurrent(t);
    setTouched(true);
    v.pause();
    v.currentTime = t;
  }

  // 微調按鈕,提高標註精度
  function nudge(delta: number) {
    const v = videoRef.current;
    if (!v) return;
    const t = Math.max(0, Math.min(duration || 0, (v.currentTime || 0) + delta));
    v.pause();
    v.currentTime = t;
    setCurrent(t);
    setTouched(true);
  }

  function togglePlay() {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  }

  async function submit() {
    setErr(null);
    if (!touched) {
      setErr('請先拖曳時間軸到停止線通過的時間點');
      return;
    }
    if (!duration) {
      setErr('影片時長尚未載入');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ time_seconds: current, video_duration: duration });
    } catch (e) {
      setErr(String(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function markUnlabelable() {
    setErr(null);
    const reason = window.prompt(
      '標記為「無法標記」的原因(可留白):\n例如:影片損毀、看不出停止線、車輛未進入路口…',
      '',
    );
    // null = 使用者取消;空字串 = 確認但不填原因
    if (reason === null) return;
    const confirmed = window.confirm('確定要將此事件標記為「無法標記」?該事件將不再出現於待標註佇列。');
    if (!confirmed) return;
    setSubmitting(true);
    try {
      const payload: { unlabelable: true; reason?: string } = { unlabelable: true };
      if (reason.trim()) payload.reason = reason.trim();
      await onMarkUnlabelable(payload);
    } catch (e) {
      setErr(String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="video-labeler">
      <div className="video-frame">
        <video
          ref={videoRef}
          src={videoUrl}
          controls={false}
          muted
          autoPlay
          playsInline
          onLoadedMetadata={onLoadedMetadata}
          onTimeUpdate={onTimeUpdate}
          onClick={togglePlay}
          onError={onVideoError}
          style={{ cursor: 'pointer' }}
        />
        {videoState === 'loading' && (
          <div className="video-overlay muted">影片載入中…</div>
        )}
        {videoState === 'error' && (
          <div className="video-overlay error-overlay">
            <div className="error">影片無法載入</div>
            <div className="muted small">{videoErr}</div>
            <div className="small">
              <a href={videoUrl} target="_blank" rel="noreferrer">
                在新分頁開啟原始連結進行除錯 ↗
              </a>
            </div>
            <div className="muted small">
              若新分頁可以播放但這裡不行 → 通常是 CORS / 來源未提供
              Access-Control-Allow-Origin。
              若新分頁也打不開 → URL 失效、需要登入、或網路問題。
            </div>
          </div>
        )}
      </div>
      <div className="timeline-row">
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.01}
          value={current}
          onChange={onScrub}
          disabled={!duration}
        />
        <span className="time-readout">
          {current.toFixed(2)} 秒 / {duration.toFixed(2)} 秒
        </span>
      </div>
      <div className="action-row">
        <div className="nudge-buttons">
          <button type="button" onClick={() => nudge(-1)}>-1 秒</button>
          <button type="button" onClick={() => nudge(-0.1)}>-0.1 秒</button>
          <button type="button" onClick={() => nudge(-0.033)}>-1 幀</button>
          <button type="button" onClick={() => nudge(0.033)}>+1 幀</button>
          <button type="button" onClick={() => nudge(0.1)}>+0.1 秒</button>
          <button type="button" onClick={() => nudge(1)}>+1 秒</button>
        </div>
        <div className="submit-area">
          {err && <span className="error">{err}</span>}
          <button
            type="button"
            className="unlabelable-btn"
            onClick={markUnlabelable}
            disabled={submitting}
          >
            無法標記
          </button>
          <button onClick={submit} disabled={submitting || !touched}>
            {submitting ? '儲存中…' : '送出並繼續'}
          </button>
        </div>
      </div>
    </div>
  );
}
